import { motion } from 'framer-motion'

const ProductCard = ({ product }) => {
  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      className="bg-white rounded-2xl shadow-lg overflow-hidden card-hover"
    >
      {/* Container avec image de fond et icône par-dessus */}
      <div className="h-48 relative bg-gradient-to-br from-blue-500 to-green-300 flex items-center justify-center overflow-hidden">
        {/* Image de fond - plus claire */}
        <img 
          src={product.image} 
          alt={product.name}
          className="absolute inset-0 w-full h-full object-cover opacity-30 filter brightness-110"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        {/* Icône au premier plan */}
        <span className="text-6xl relative z-10">{product.icon}</span>
      </div>
      <div className="p-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">{product.name}</h3>
        <p className="text-gray-600 leading-relaxed">{product.description}</p>
      </div>
    </motion.div>
  )
}

// Composant principal ECOTHERM
const EcothermHero = () => {
  // Images par défaut ou gestion des erreurs
  const defaultProductImage = "https://via.placeholder.com/400x300/4ADE80/FFFFFF?text=ECOTHERM+PRODUIT";
  const defaultApplicationImages = [
    "images/Dry-façade-system.jpg",
    "images/R (1).jpeg",
    "images/R (2).jpeg",
    "images/OIP (7).webp"
  ];

  return (
    <div className="w-full bg-gradient-to-br from-green-50 to-white">
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full py-12 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-7xl mx-auto">
          {/* En-tête */}
          <div className="text-center mb-12">
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-4xl md:text-6xl font-bold text-green-800 mb-4"
            >
              ECOTHERM
            </motion.h1>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-xl md:text-2xl text-green-600 font-semibold"
            >
              <p>100% NATUREL • PRÊT À L'EMPLOI</p>
            </motion.div>
          </div>

          {/* Contenu principal */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Colonne de gauche - Image avec nouveau style */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-green-100 to-green-50 rounded-2xl shadow-2xl p-8  hover:rotate-0 transition-transform duration-300 border border-green-200">
                <div className="relative group">
                  <img
                    src="images/ecotherm.png"
                    alt="Produit ECOTHERM"
                    className="w-full h-auto rounded-xl shadow-lg object-cover transform group-hover:scale-105 transition-transform duration-500 filter brightness-105 contrast-110"
                    style={{ maxHeight: '400px', objectFit: 'contain' }}
                    onError={(e) => {
                      e.target.src = defaultProductImage;
                    }}
                  />
                  {/* Overlay d'effet */}
                  <div className="absolute inset-0 bg-gradient-to-t from-green-50/20 to-transparent rounded-xl group-hover:from-green-100/30 transition-all duration-500"></div>
                </div>
                {/* Badge */}
                <div className="absolute -top-3 -right-3 bg-green-600 text-white px-4 py-2 rounded-full shadow-lg transform rotate-6">
                  <span className="text-sm font-bold">NOUVEAU</span>
                </div>
              </div>
            </motion.div>

            {/* Colonne de droite - Caractéristiques */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.9 }}
              className="space-y-6"
            >
              {/* Grille des caractéristiques */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: "🔥", text: "isolation thermique" },
                  { icon: "🛡️", text: "ignifuge" },
                  { icon: "🔇", text: "insonorisant" },
                  { icon: "💨", text: "produit respirant" },
                  { icon: "☀️", text: "protection UV A/B" },
                  { icon: "🌱", text: "100% naturel" }
                ].map((feature, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.05, y: -2 }}
                    className="bg-white rounded-xl p-4 shadow-md border border-green-100 flex items-center space-x-3 hover:shadow-lg transition-all duration-300"
                  >
                    <span className="text-2xl">{feature.icon}</span>
                    <span className="text-gray-700 font-medium capitalize">
                      {feature.text}
                    </span>
                  </motion.div>
                ))}
              </div>

              {/* Sections informatives */}
              <div className="space-y-4">
                <div className="bg-green-50 rounded-xl p-6 border border-green-200 hover:shadow-md transition-all duration-300">
                  <h3 className="text-lg font-semibold text-green-800 mb-2">
                    🌍 Respect de l'environnement
                  </h3>
                  <p className="text-gray-700">
                    Ses composants sont 100% naturels et répondent parfaitement aux défis du changement climatique.
                  </p>
                </div>

                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200 hover:shadow-md transition-all duration-300">
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">
                    💧 Sans ajout d'eau
                  </h3>
                  <p className="text-gray-700">
                    C'est la réponse parfaite à l'un des maux de notre siècle : la pénurie d'eau.
                  </p>
                </div>

                <div className="bg-orange-50 rounded-xl p-6 border border-orange-200 hover:shadow-md transition-all duration-300">
                  <h3 className="text-lg font-semibold text-orange-800 mb-2">
                    🏗️ Large gamme d'applications
                  </h3>
                  <p className="text-gray-700">
                    Notre produit peut être utilisé pour les applications les plus simples comme les plus sensibles.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Section Compatibilité Universelle */}
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.1 }}
        className="w-full bg-gradient-to-br from-green-100 to-green-50 py-16 px-4 sm:px-6 lg:px-8 border-y border-green-200"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3 }}
              className="inline-block bg-green-600/10 border border-green-400/30 rounded-full px-6 py-2 mb-4"
            >
              <span className="text-green-700 font-semibold text-sm uppercase tracking-wider">
                Compatibilité Universelle
              </span>
            </motion.div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Adhère à tous les types de matériaux
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Grâce à un procédé tenu secret, ECOTHERM s'adapte parfaitement à toutes les surfaces
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Liste des matériaux compatibles */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 1.4 }}
              className="space-y-6"
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-green-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  🏗️ Matériaux de Construction
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    "Brique traditionnelle",
                    "Moellons et pierre",
                    "Béton et béton armé",
                    "Béton cellulaire",
                    "Bois (OSB, contreplaqué)",
                    "Acier et métaux",
                    "Conteneurs maritimes",
                    "Surfaces métalliques"
                  ].map((material, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ scale: 1.02 }}
                      className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors duration-300"
                    >
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm">✓</span>
                      </div>
                      <span className="text-gray-700 font-medium text-sm">{material}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Applications spécialisées */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 1.6 }}
              className="space-y-6"
            >
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 shadow-lg border border-blue-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  🔥 Applications Techniques
                </h3>
                <div className="space-y-4">
                  {[
                    {
                      icon: "📦",
                      title: "Calorifugeage des conduites",
                      description: "Isolation des canalisations de chauffage et d'eau chaude sanitaire pour réduire les pertes de chaleur"
                    },
                    {
                      icon: "💰",
                      title: "Économies d'énergie",
                      description: "Réduction des déperditions dans les zones non chauffées (sous-sols, garages)"
                    },
                    {
                      icon: "🏠",
                      title: "Isolation des sols",
                      description: "Application directe sous chape pour isoler du froid, sans primaire d'accrochage"
                    },
                    {
                      icon: "⏱️",
                      title: "Gain de temps et économies",
                      description: "Application directe sans préparation de surface complexe"
                    }
                  ].map((app, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ x: 5 }}
                      className="flex items-start space-x-4 p-4 bg-white/50 rounded-lg hover:bg-white/70 transition-all duration-300"
                    >
                      <span className="text-2xl flex-shrink-0">{app.icon}</span>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">{app.title}</h4>
                        <p className="text-gray-600 text-sm">{app.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Bénéfices supplémentaires */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.8 }}
            className="mt-12 bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-8 text-white shadow-2xl"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="space-y-2">
                <div className="text-3xl">🚀</div>
                <h4 className="font-bold text-lg">Application Directe</h4>
                <p className="text-green-100 text-sm">Sans primaire d'accrochage nécessaire</p>
              </div>
              <div className="space-y-2">
                <div className="text-3xl">⏰</div>
                <h4 className="font-bold text-lg">Gain de Temps</h4>
                <p className="text-green-100 text-sm">Réduction du temps d'application</p>
              </div>
              <div className="space-y-2">
                <div className="text-3xl">💸</div>
                <h4 className="font-bold text-lg">Économies</h4>
                <p className="text-green-100 text-sm">Réduction des coûts de main d'œuvre</p>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Nouvelle section professionnelle */}
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 2.0 }}
        className="w-full bg-gradient-to-r from-gray-900 to-green-900 py-16 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Texte descriptif */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 2.2 }}
              className="space-y-6"
            >
              <div className="inline-block bg-green-600/20 border border-green-500/30 rounded-full px-6 py-2 mb-4">
                <span className="text-green-300 font-semibold text-sm uppercase tracking-wider">
                  Solution Professionnelle
                </span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                Une solution complète pour 
                <span className="text-green-300"> l'isolation et la protection</span>
              </h2>
              
              <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
                <p>
                  <strong className="text-white">Polyvalent et performant</strong>, ce produit s'adapte aussi bien aux projets neufs qu'à la rénovation.
                </p>
                
                <p>
                  <strong className="text-white">Conçu pour résister aux contraintes extérieures</strong>, il offre une excellente tenue aux intempéries et aux variations climatiques.
                </p>
                
                <p>
                  <strong className="text-white">Application intérieure et extérieure</strong> : il peut être appliqué sur les murs donnant sur l'extérieur, où il assure une protection et une isolation durables tout en préservant l'esthétique de la surface.
                </p>
              </div>

              {/* Points clés */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                {[
                  { icon: "🔄", text: "Neuf & Rénovation" },
                  { icon: "🌧️", text: "Résistant aux intempéries" },
                  { icon: "🏠", text: "Intérieur/Extérieur" },
                  { icon: "🎨", text: "Esthétique préservée" }
                ].map((point, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ x: 5 }}
                    className="flex items-center space-x-3 group"
                  >
                    <div className="w-10 h-10 bg-green-600/20 rounded-full flex items-center justify-center group-hover:bg-green-600/30 transition-colors duration-300">
                      <span className="text-lg">{point.icon}</span>
                    </div>
                    <span className="text-white font-medium">{point.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Image illustrative */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 2.4 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-green-800/20 to-gray-800/20 rounded-2xl p-8 border border-green-700/30 backdrop-blur-sm">
                <div className="grid grid-cols-2 gap-4">
                  {defaultApplicationImages.map((imgSrc, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ scale: 1.05 }}
                      className="relative h-40 bg-gray-800 rounded-xl overflow-hidden group"
                    >
                      <img
                        src={imgSrc}
                        alt={`Application ECOTHERM ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = `https://via.placeholder.com/300x200/475569/FFFFFF?text=Image+${index + 1}`;
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"></div>
                    </motion.div>
                  ))}
                </div>
                
                {/* Badge de qualité */}
                <div className="absolute -bottom-4 -right-4 bg-green-600 text-white px-6 py-3 rounded-xl shadow-2xl">
                  <div className="text-center">
                    <div className="font-bold text-lg">QUALITÉ</div>
                    <div className="text-xs opacity-90">PROFESSIONNELLE</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>
    </div>
  )
}

export default ProductCard
export { EcothermHero }